/**
 * ManjaroBooksPage — Standalone Public E-Reading Platform
 * Browse, purchase, and read books in-browser. No auth for browsing; reader auth for purchasing/reading.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  BookOpen, Search, Star, ShoppingCart, Heart, User, LogIn, LogOut,
  ChevronLeft, ChevronRight, Menu, X, BookMarked, Clock, Library,
  Bookmark, ArrowRight, Shield, Eye, Filter, Grid3X3, List, Rocket, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_TENANT_ID = '69d63c2c39ded375827179d9';

// Module-level mutable tenant id. The root <ManjaroBooksPage> sets this from
// the URL slug (or falls back to DEFAULT_TENANT_ID for the legacy alias).
// This lets every nested component keep its plain `pubApi('/path')` calls
// without threading tenant through every component prop.
let activeTenantId = (typeof localStorage !== 'undefined' && localStorage.getItem('manjoro_tenant_id')) || DEFAULT_TENANT_ID;
const setActiveTenantId = (id) => {
  if (!id) return;
  activeTenantId = id;
  try { localStorage.setItem('manjoro_tenant_id', id); } catch { /* ignore */ }
};

const pubApi = async (path) => {
  const res = await fetch(`${API_URL}/api/books/public/${activeTenantId}${path}`);
  if (!res.ok) throw new Error('Request failed');
  return res.json();
};

const readerApi = async (path, opts = {}) => {
  const token = localStorage.getItem('manjoro_reader_token') || '';
  const res = await fetch(`${API_URL}/api/books/reader/${activeTenantId}${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

// ==================== CONTEXT ====================
const useReader = () => {
  const [reader, setReader] = useState(() => {
    try { return JSON.parse(localStorage.getItem('manjoro_reader') || 'null'); } catch { return null; }
  });
  const login = (data) => {
    localStorage.setItem('manjoro_reader_token', data.token);
    localStorage.setItem('manjoro_reader', JSON.stringify(data.reader));
    setReader(data.reader);
  };
  const logout = () => {
    localStorage.removeItem('manjoro_reader_token');
    localStorage.removeItem('manjoro_reader');
    setReader(null);
  };
  return { reader, login, logout, isLoggedIn: !!reader };
};

// ==================== NAV ====================
const StoreNav = ({ section, setSection, reader, logout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data: settings } = useQuery({ queryKey: ['book-settings'], queryFn: () => pubApi('/settings') });
  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'store', label: 'Store' },
  ];
  if (reader) navItems.push({ id: 'library', label: 'My Library' });

  return (
    <nav className="sticky top-0 z-50 bg-[var(--ms-primary,#0c0a09)] border-b border-[var(--ms-border,#292524)]" data-testid="store-nav">
      <div className="max-w-6xl mx-auto px-4 flex items-center justify-between h-14">
        <button onClick={() => setSection('home')} className="flex items-center gap-2" data-testid="store-logo">
          {settings?.logo_url ? (
            <img src={settings.logo_url} alt={settings.store_name || 'Logo'} className="h-7 w-7 object-contain rounded" />
          ) : (
            <BookOpen className="h-6 w-6 text-[var(--ms-accent,#c9a227)]" />
          )}
          <span className="font-bold text-[var(--ms-accent,#c9a227)] text-sm tracking-wide">{settings?.store_name || 'ABMB Books'}</span>
        </button>
        <div className="hidden md:flex items-center gap-1">
          {navItems.map(n => (
            <button key={n.id} onClick={() => setSection(n.id)}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                section === n.id ? 'bg-[var(--ms-accent,#c9a227)]/20 text-[var(--ms-accent,#c9a227)] font-medium' : 'text-[var(--ms-text-light,#e7e5e4)] hover:text-white'
              }`} data-testid={`nav-${n.id}`}>{n.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {reader ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-[var(--ms-text-light,#e7e5e4)] hidden sm:block">{reader.name || reader.email}</span>
              <button onClick={logout} className="text-[var(--ms-text-light,#e7e5e4)] hover:text-white" data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button onClick={() => setSection('auth')} className="flex items-center gap-1 px-3 py-1.5 bg-[var(--ms-accent,#c9a227)] text-[var(--ms-dark,#0c0a09)] rounded text-sm font-medium hover:bg-[var(--ms-accent-hover,#a98317)]" data-testid="signin-btn">
              <LogIn className="h-4 w-4" /> Sign In
            </button>
          )}
          <button className="md:hidden text-[var(--ms-text-light,#e7e5e4)]" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="md:hidden bg-[var(--ms-dark-2,#1c1917)] border-t border-[var(--ms-border,#292524)] p-3 space-y-1">
          {navItems.map(n => (
            <button key={n.id} onClick={() => { setSection(n.id); setMobileOpen(false); }}
              className={`block w-full text-left px-3 py-2 rounded text-sm ${
                section === n.id ? 'bg-[var(--ms-accent,#c9a227)]/20 text-[var(--ms-accent,#c9a227)]' : 'text-[var(--ms-text-light,#e7e5e4)] hover:bg-[var(--ms-border,#292524)]'
              }`}>{n.label}</button>
          ))}
        </div>
      )}
    </nav>
  );
};

// ==================== HOME ====================
// ==================== EDITORIAL ROW (Bestsellers / New / Free) ====================
const EditorialRow = ({ title, eyebrow, books, setSection, accent, muted }) => {
  if (!books || books.length === 0) return null;
  const bg = accent ? 'bg-[#fafaf9]' : muted ? 'bg-[var(--ms-dark,#0c0a09)] text-white' : 'bg-white';
  const dark = !!muted;
  return (
    <div className={`${bg} py-16`} data-testid={`editorial-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-baseline justify-between mb-8 flex-wrap gap-2">
          <div>
            <p className={`text-[10px] uppercase tracking-[0.3em] font-semibold ${dark ? 'text-[var(--ms-accent,#c9a227)]' : 'text-[var(--ms-accent,#c9a227)]'}`}>{eyebrow}</p>
            <h2 className={`text-3xl font-serif font-bold mt-1 ${dark ? 'text-white' : 'text-[var(--ms-dark,#0c0a09)]'}`}>{title}</h2>
          </div>
          <button
            onClick={() => setSection('store')}
            className={`hidden sm:flex items-center gap-1 text-sm font-semibold transition ${dark ? 'text-[var(--ms-text-light,#e7e5e4)] hover:text-white' : 'text-[var(--ms-primary,#7c5e3a)] hover:text-[var(--ms-dark,#0c0a09)]'}`}
            data-testid={`editorial-viewall-${title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            View all <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
          {books.map((book) => (
            <BookCard key={book.id} book={book} onSelect={() => setSection(`book:${book.id}`)} />
          ))}
        </div>
      </div>
    </div>
  );
};

const HomeSection = ({ setSection }) => {
  const { data: settings } = useQuery({ queryKey: ['book-settings'], queryFn: () => pubApi('/settings') });
  const { data: featured = [] } = useQuery({ queryKey: ['book-featured'], queryFn: () => pubApi('/featured') });
  const { data: stats } = useQuery({ queryKey: ['book-stats'], queryFn: () => pubApi('/stats') });
  const { data: catalog = [] } = useQuery({ queryKey: ['book-catalog'], queryFn: () => pubApi('/catalog') });
  const { data: categories = [] } = useQuery({ queryKey: ['book-categories'], queryFn: () => pubApi('/categories') });
  const [heroSearch, setHeroSearch] = useState('');

  // Editorial sections — each is a slice of the catalog for a curated feel
  const newArrivals = React.useMemo(
    () => [...catalog].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 6),
    [catalog],
  );
  const freeReads = React.useMemo(
    () => catalog.filter((b) => b.is_free).slice(0, 6),
    [catalog],
  );
  const bestsellers = React.useMemo(
    () => [...catalog].sort((a, b) => (b.rentals_count || b.purchases_count || 0) - (a.rentals_count || a.purchases_count || 0)).slice(0, 6),
    [catalog],
  );

  const onSearch = (e) => {
    e.preventDefault();
    // Persist search for the store section to pick up
    try { sessionStorage.setItem('books_initial_search', heroSearch); } catch {}
    setSection('store');
  };

  return (
    <div data-testid="home-section">
      {/* Hero */}
      <div
        className="relative bg-gradient-to-b from-[var(--ms-dark,#0c0a09)] via-[var(--ms-dark-2,#1c1917)] to-[var(--ms-dark-3,#0a0a0a)] py-20 sm:py-28 bg-cover bg-center"
        style={settings?.hero_image_url ? { backgroundImage: `linear-gradient(rgba(44,24,16,0.78), rgba(26,15,10,0.85)), url(${settings.hero_image_url})` } : undefined}
        data-testid="store-hero"
      >
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-[var(--ms-accent,#c9a227)] tracking-[0.3em] text-xs uppercase mb-4">{settings?.store_name || 'ABMB Books'}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white leading-[1.05] tracking-tight">
            {settings?.store_tagline ? (
              settings.store_tagline
            ) : (
              <>Read Transformative <span className="text-[var(--ms-accent,#c9a227)] italic">Christian</span> Literature</>
            )}
          </h1>
          <p className="text-[var(--ms-text-light,#e7e5e4)] mt-4 max-w-xl mx-auto text-base">
            {settings?.store_description || 'Access a growing collection of inspiring books directly in your browser. Purchase, rent, or subscribe to read anytime, anywhere.'}
          </p>

          {/* Search bar — inspired by Eden / Blackwell's / Hive */}
          <form onSubmit={onSearch} className="mt-8 max-w-xl mx-auto" data-testid="hero-search-form">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#78716c]" />
              <Input
                placeholder="Search books, authors, topics…"
                value={heroSearch}
                onChange={(e) => setHeroSearch(e.target.value)}
                className="pl-12 pr-32 h-14 text-base bg-white text-[var(--ms-dark,#0c0a09)] border-0 shadow-xl rounded-full"
                data-testid="hero-search-input"
              />
              <Button
                type="submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-[var(--ms-accent,#c9a227)] text-[var(--ms-dark,#0c0a09)] hover:bg-[var(--ms-accent-hover,#a98317)] font-semibold rounded-full px-5 h-11"
                data-testid="hero-search-submit"
              >
                Search
              </Button>
            </div>
          </form>

          <div className="flex gap-3 justify-center mt-5">
            <Button variant="link" className="text-[var(--ms-text-light,#e7e5e4)] hover:text-white" onClick={() => setSection('store')} data-testid="hero-browse-btn">
              <Library className="h-4 w-4 mr-1.5" /> Browse all books
            </Button>
            <Button variant="link" className="text-[var(--ms-text-light,#e7e5e4)] hover:text-white" onClick={() => setSection('auth')}>
              Sign in
            </Button>
          </div>
          {stats && (
            <div className="flex gap-8 sm:gap-16 justify-center mt-12 text-center">
              <div><p className="text-2xl font-bold text-[var(--ms-accent,#c9a227)] italic">{stats.total_books}+</p><p className="text-xs text-[var(--ms-text-light,#e7e5e4)]">Books Available</p></div>
              <div><p className="text-2xl font-bold text-[var(--ms-accent,#c9a227)] italic">&infin;</p><p className="text-xs text-[var(--ms-text-light,#e7e5e4)]">Read Anytime</p></div>
              <div><p className="text-2xl font-bold text-[var(--ms-accent,#c9a227)] italic">24/7</p><p className="text-xs text-[var(--ms-text-light,#e7e5e4)]">Instant Access</p></div>
            </div>
          )}
        </div>
      </div>

      {/* Categories chip row — inspired by Waterstones / bookshop.org */}
      {categories.length > 0 && (
        <div className="bg-white border-b border-[var(--ms-border-light,#e7e5e4)] py-5">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-3 overflow-x-auto pb-1" data-testid="category-chips">
              <span className="text-xs uppercase tracking-widest text-[#78716c] font-semibold shrink-0">Browse by topic</span>
              {categories.slice(0, 12).map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    try { sessionStorage.setItem('books_initial_category', c.name); } catch {}
                    setSection('store');
                  }}
                  className="shrink-0 px-3 py-1.5 rounded-full border border-[var(--ms-border-light,#e7e5e4)] text-xs font-medium text-[var(--ms-dark,#0c0a09)] hover:bg-[var(--ms-accent,#c9a227)]/10 hover:border-[var(--ms-accent,#c9a227)] transition"
                  data-testid={`category-chip-${c.id}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* How It Works */}
      <div className="bg-[#fafaf9] py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-lg font-bold text-[var(--ms-dark,#0c0a09)] text-center mb-8">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Choose Your Book', desc: 'Browse our collection and select from purchase, rental, or free options.', icon: BookOpen },
              { step: '2', title: 'Get Access', desc: 'Create a free account and purchase or get free books instantly.', icon: ShoppingCart },
              { step: '3', title: 'Start Reading', desc: 'Access your book in our web reader. Read on any device, save your progress.', icon: Eye },
            ].map(s => (
              <div key={s.step} className="text-center p-6 bg-white rounded-xl border border-[var(--ms-border-light,#e7e5e4)]">
                <div className="w-12 h-12 rounded-full bg-[var(--ms-accent,#c9a227)]/10 flex items-center justify-center mx-auto mb-3">
                  <s.icon className="h-6 w-6 text-[var(--ms-accent,#c9a227)]" />
                </div>
                <p className="text-xs text-[var(--ms-accent,#c9a227)] font-semibold mb-1">Step {s.step}</p>
                <h3 className="font-semibold text-sm text-[var(--ms-dark,#0c0a09)]">{s.title}</h3>
                <p className="text-xs text-[#78716c] mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Featured Books */}
      {featured.length > 0 && (
        <div className="bg-white py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-baseline justify-between mb-8">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--ms-accent,#c9a227)] font-semibold">Hand-picked</p>
                <h2 className="text-3xl font-serif font-bold text-[var(--ms-dark,#0c0a09)] mt-1">Featured Books</h2>
              </div>
              <button onClick={() => setSection('store')} className="hidden sm:flex items-center gap-1 text-sm text-[var(--ms-primary,#7c5e3a)] hover:text-[var(--ms-dark,#0c0a09)] font-semibold transition">
                View all <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
              {featured.map(book => (
                <BookCard key={book.id} book={book} onSelect={() => setSection(`book:${book.id}`)} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Editorial sections — inspired by Waterstones / Blackwell's / Eden */}
      <EditorialRow title="Best Sellers" eyebrow="Most read this season" books={bestsellers} setSection={setSection} accent />
      <EditorialRow title="New Arrivals" eyebrow="Fresh on the shelves" books={newArrivals} setSection={setSection} />
      {freeReads.length > 0 && (
        <EditorialRow title="Free to Read" eyebrow="No charge — start reading now" books={freeReads} setSection={setSection} muted />
      )}

      {/* New Releases */}
      <NewReleasesSection setSection={setSection} />

      {/* Testimonials */}
      <div className="bg-[var(--ms-primary,#0c0a09)] py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-lg font-bold text-white text-center mb-8">What Readers Say</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { quote: 'The convenience of reading directly in my browser while keeping my progress saved is amazing.', name: 'Grace M.', role: 'Reader' },
              { quote: "Archbishop Manjoro's teachings have transformed my understanding of ministry.", name: 'Pastor John K.', role: 'Church Leader' },
              { quote: 'I love the rental option — perfect for books I want to study intensively for a season.', name: 'Sarah T.', role: 'Student' },
            ].map((t, i) => (
              <div key={i} className="bg-[var(--ms-dark-2,#1c1917)] rounded-xl p-5 border border-[var(--ms-border,#292524)]">
                <p className="text-sm text-[var(--ms-text-light,#e7e5e4)] italic leading-relaxed">"{t.quote}"</p>
                <div className="mt-4 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[var(--ms-accent,#c9a227)]/20 flex items-center justify-center text-[var(--ms-accent,#c9a227)] text-xs font-bold">{t.name[0]}</div>
                  <div><p className="text-white text-xs font-medium">{t.name}</p><p className="text-[#78716c] text-[10px]">{t.role}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-[#fafaf9] py-16 text-center">
        <h2 className="text-xl font-bold text-[var(--ms-dark,#0c0a09)] mb-3">Ready to Start Reading?</h2>
        <p className="text-sm text-[#78716c] mb-6">Join readers who are growing through our collection of transformative books.</p>
        <div className="flex gap-3 justify-center">
          <Button className="bg-[var(--ms-primary,#7c5e3a)] text-white hover:bg-[var(--ms-primary-hover,#5c4628)]" onClick={() => setSection('store')}>Explore Collection</Button>
          <Button variant="outline" className="border-[var(--ms-primary,#7c5e3a)] text-[var(--ms-primary,#7c5e3a)]" onClick={() => setSection('auth')}>Create Free Account</Button>
        </div>
      </div>
    </div>
  );
};

// ==================== NEW RELEASES ====================
const NewReleasesSection = ({ setSection }) => {
  const { data: launches = [] } = useQuery({ queryKey: ['book-launches'], queryFn: () => pubApi('/launches') });
  const { data: allBooks = [] } = useQuery({ queryKey: ['book-catalog-for-launches'], queryFn: () => pubApi('/catalog') });
  const reader = JSON.parse(localStorage.getItem('manjoro_reader') || 'null');
  const [preOrdering, setPreOrdering] = useState(null);

  const handlePreOrder = async (launch) => {
    if (!reader) {
      toast.error('Sign in to pre-order');
      setSection('auth');
      return;
    }
    const bookId = launch.book_id;
    if (!bookId) {
      toast.error('This launch is not linked to a book yet');
      return;
    }
    setPreOrdering(launch.id);
    try {
      const res = await fetch(`${API_URL}/api/stripe/checkout/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('manjoro_reader_token') || ''}`,
        },
        body: JSON.stringify({
          tenant_id: activeTenantId,
          book_id: bookId,
          access_type: 'purchase',
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Checkout failed');
      }
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.message);
    }
    setPreOrdering(null);
  };

  if (launches.length === 0) return null;

  return (
    <div className="bg-gradient-to-b from-[#fafaf9] to-white py-16" data-testid="new-releases-section">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[var(--ms-accent,#c9a227)]/10 px-4 py-1.5 rounded-full mb-3">
            <Rocket className="h-4 w-4 text-[var(--ms-accent,#c9a227)]" />
            <span className="text-xs font-semibold text-[var(--ms-accent,#c9a227)] uppercase tracking-wider">New Releases</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-[var(--ms-dark,#0c0a09)]">Just Launched</h2>
          <p className="text-sm text-[#78716c] mt-2 max-w-lg mx-auto">Discover our latest releases — fresh titles now available for pre-order and purchase.</p>
        </div>

        <div className="space-y-8">
          {launches.map(launch => {
            const linkedBook = allBooks.find(b => b.id === launch.book_id);
            const coverUrl = launch.cover_image_url || linkedBook?.cover_image_url;
            const launchDate = launch.launch_date ? new Date(launch.launch_date) : null;
            const isUpcoming = launchDate && launchDate > new Date();

            return (
              <div key={launch.id} className="bg-white rounded-2xl border border-[var(--ms-border-light,#e7e5e4)] overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`launch-card-${launch.id}`}>
                {launch.banner_image_url && (
                  <div className="h-44 sm:h-56 bg-gradient-to-r from-[var(--ms-dark,#0c0a09)] to-[var(--ms-primary,#7c5e3a)] relative overflow-hidden">
                    <img src={launch.banner_image_url} alt="" className="w-full h-full object-cover opacity-80" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-6">
                      <Badge className="bg-[var(--ms-accent,#c9a227)] text-[var(--ms-dark,#0c0a09)] text-xs font-semibold">
                        <Sparkles className="h-3 w-3 mr-1" /> New Release
                      </Badge>
                    </div>
                  </div>
                )}
                <div className="p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row gap-6">
                    {/* Cover */}
                    <div className="w-32 sm:w-40 shrink-0">
                      <div className="aspect-[2/3] rounded-lg bg-gradient-to-br from-[var(--ms-primary,#7c5e3a)] to-[var(--ms-dark,#0c0a09)] overflow-hidden shadow-lg">
                        {coverUrl ? (
                          <img src={coverUrl} alt={launch.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <BookOpen className="h-12 w-12 text-[var(--ms-accent,#c9a227)]/40" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-xl font-bold text-[var(--ms-dark,#0c0a09)]">{launch.title}</h3>
                      {linkedBook && <p className="text-sm text-[#78716c] mt-0.5">by {linkedBook.author}</p>}
                      <p className="text-sm text-[#5D4E37] mt-3 leading-relaxed line-clamp-3">{launch.description}</p>

                      {launch.features?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-4">
                          {launch.features.map((f, i) => (
                            <span key={i} className="text-[10px] bg-[#fafaf9] text-[#78716c] border border-[var(--ms-border-light,#e7e5e4)] px-2 py-0.5 rounded-full">{f}</span>
                          ))}
                        </div>
                      )}

                      {launch.author_message && (
                        <div className="mt-4 bg-[#fafaf9] rounded-lg p-3 border-l-3 border-[var(--ms-accent,#c9a227)]" style={{ borderLeftWidth: 3, borderLeftColor: '#c9a227' }}>
                          <p className="text-xs text-[#5D4E37] italic">"{launch.author_message}"</p>
                          <p className="text-[10px] text-[#78716c] mt-1 font-medium">— Author's Note</p>
                        </div>
                      )}

                      <div className="flex items-center gap-4 mt-5">
                        {launchDate && (
                          <div className="flex items-center gap-1.5 text-xs text-[#78716c]">
                            <Clock className="h-3.5 w-3.5" />
                            <span>{isUpcoming ? 'Launches' : 'Launched'} {launchDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                          </div>
                        )}
                        {launch.pre_order_price > 0 && (
                          <span className="text-lg font-bold text-[var(--ms-primary,#7c5e3a)]">${launch.pre_order_price.toFixed(2)}</span>
                        )}
                        {linkedBook && !launch.pre_order_price && (
                          <span className="text-lg font-bold text-[var(--ms-primary,#7c5e3a)]">${linkedBook.price?.toFixed(2)}</span>
                        )}
                      </div>

                      <div className="flex gap-3 mt-4">
                        {launch.book_id && (
                          <Button
                            className="bg-[var(--ms-accent,#c9a227)] text-[var(--ms-dark,#0c0a09)] hover:bg-[var(--ms-accent-hover,#a98317)] font-semibold"
                            onClick={() => handlePreOrder(launch)}
                            disabled={preOrdering === launch.id}
                            data-testid={`preorder-btn-${launch.id}`}
                          >
                            {preOrdering === launch.id ? (
                              <><Clock className="h-4 w-4 mr-1.5 animate-spin" /> Processing...</>
                            ) : (
                              <><ShoppingCart className="h-4 w-4 mr-1.5" /> {isUpcoming ? 'Pre-Order Now' : 'Buy Now'}</>
                            )}
                          </Button>
                        )}
                        {launch.book_id && (
                          <Button variant="outline" className="border-[var(--ms-primary,#7c5e3a)] text-[var(--ms-primary,#7c5e3a)] hover:bg-[var(--ms-primary,#7c5e3a)] hover:text-white"
                            onClick={() => setSection(`book:${launch.book_id}`)} data-testid={`view-launch-book-${launch.id}`}>
                            <Eye className="h-4 w-4 mr-1.5" /> View Book
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ==================== BOOK CARD ====================
const BookCard = ({ book, onSelect }) => (
  <div
    className="group cursor-pointer transition-all duration-300 hover:-translate-y-1"
    onClick={onSelect}
    data-testid={`book-card-${book.id}`}
  >
    {/* Cover with book-spine proportions */}
    <div className="relative aspect-[3/4] rounded-md overflow-hidden shadow-lg group-hover:shadow-2xl transition-shadow bg-gradient-to-br from-[var(--ms-primary,#7c5e3a)] to-[var(--ms-dark,#0c0a09)]">
      {book.cover_image_url ? (
        <img
          src={book.cover_image_url}
          alt={book.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
          <BookOpen className="h-8 w-8 text-[var(--ms-accent,#c9a227)]/60 mb-3" />
          <p className="text-[var(--ms-accent,#c9a227)] text-xs font-serif italic leading-tight line-clamp-3">{book.title}</p>
        </div>
      )}
      {/* Subtle gradient overlay for legibility */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
      {book.is_free && (
        <Badge className="absolute top-2 left-2 bg-emerald-600 text-white text-[10px] uppercase tracking-wider font-bold shadow-md">
          Free
        </Badge>
      )}
      {book.is_featured && (
        <Badge className="absolute top-2 right-2 bg-[var(--ms-accent,#c9a227)] text-[var(--ms-dark,#0c0a09)] text-[10px] uppercase tracking-wider font-bold shadow-md">
          Featured
        </Badge>
      )}
    </div>
    {/* Title and metadata below cover */}
    <div className="pt-3 px-1">
      <p className="text-[10px] uppercase tracking-widest text-[#78716c] mb-1">{book.category || 'Christian Living'}</p>
      <h3 className="font-serif font-semibold text-sm text-[var(--ms-dark,#0c0a09)] line-clamp-2 leading-snug">{book.title}</h3>
      <p className="text-xs text-[#78716c] mt-0.5 italic">{book.author}</p>
      <div className="flex items-baseline justify-between mt-2">
        {book.is_free ? (
          <span className="text-emerald-700 font-semibold text-sm">Free</span>
        ) : (
          <span className="text-[var(--ms-dark,#0c0a09)] font-bold text-base">${book.price}</span>
        )}
        <span className="text-[10px] text-[#78716c]">{book.total_pages} pages</span>
      </div>
    </div>
  </div>
);

// ==================== STORE ====================
const StoreSection = ({ setSection }) => {
  const { data: books = [], isLoading } = useQuery({ queryKey: ['book-catalog'], queryFn: () => pubApi('/catalog') });
  const { data: categories = [] } = useQuery({ queryKey: ['book-categories'], queryFn: () => pubApi('/categories') });
  // Read carry-over filters from sessionStorage so the hero search and the
  // home page category chips actually land somewhere useful.
  const [search, setSearch] = useState(() => {
    try {
      const v = sessionStorage.getItem('books_initial_search') || '';
      sessionStorage.removeItem('books_initial_search');
      return v;
    } catch { return ''; }
  });
  const [catFilter, setCatFilter] = useState(() => {
    try {
      const v = sessionStorage.getItem('books_initial_category') || '';
      sessionStorage.removeItem('books_initial_category');
      return v;
    } catch { return ''; }
  });
  const [sortBy, setSortBy] = useState('newest');

  const filtered = books.filter(b => {
    const matchSearch = !search || b.title.toLowerCase().includes(search.toLowerCase()) || b.author?.toLowerCase().includes(search.toLowerCase());
    const matchCat = !catFilter || b.category === catFilter;
    return matchSearch && matchCat;
  }).sort((a, b) => {
    if (sortBy === 'price_low') return (a.price || 0) - (b.price || 0);
    if (sortBy === 'price_high') return (b.price || 0) - (a.price || 0);
    if (sortBy === 'title') return a.title.localeCompare(b.title);
    return 0; // newest is default from API
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 space-y-8" data-testid="store-section">
      <div className="border-b border-[var(--ms-border-light,#e7e5e4)] pb-6">
        <h1 className="text-4xl font-serif font-bold text-[var(--ms-dark,#0c0a09)]">Book Store</h1>
        <p className="text-sm text-[#78716c] mt-1">Explore our collection of transformative books</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#78716c]" />
          <Input placeholder="Search books..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 border-[var(--ms-border-light,#e7e5e4)] bg-white" data-testid="search-input" />
        </div>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          className="px-3 py-2 border border-[var(--ms-border-light,#e7e5e4)] rounded-lg text-sm bg-white text-[var(--ms-dark,#0c0a09)]" data-testid="category-filter">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="px-3 py-2 border border-[var(--ms-border-light,#e7e5e4)] rounded-lg text-sm bg-white text-[var(--ms-dark,#0c0a09)]" data-testid="sort-select">
          <option value="newest">Newest First</option>
          <option value="price_low">Price: Low to High</option>
          <option value="price_high">Price: High to Low</option>
          <option value="title">Title A-Z</option>
        </select>
      </div>

      {/* Book Grid */}
      {isLoading ? (
        <div className="text-center py-16 text-[#78716c]">Loading books...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="h-12 w-12 text-[var(--ms-border-light,#e7e5e4)] mx-auto mb-3" />
          <p className="text-[#78716c]">No books found matching your criteria.</p>
          <button onClick={() => { setSearch(''); setCatFilter(''); }} className="text-[var(--ms-primary,#7c5e3a)] text-sm mt-2 underline">Clear Filters</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
          {filtered.map(book => (
            <BookCard key={book.id} book={book} onSelect={() => setSection(`book:${book.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== BOOK DETAIL ====================
const BookDetailSection = ({ bookId, setSection, reader }) => {
  const { data: book, isLoading } = useQuery({ queryKey: ['book-detail', bookId], queryFn: () => pubApi(`/book/${bookId}`) });
  const qc = useQueryClient();

  const purchaseMutation = useMutation({
    mutationFn: (data) => readerApi(`/book/${bookId}/purchase`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Access granted! You can now read this book.'); qc.invalidateQueries({ queryKey: ['reader-library'] }); },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading) return <div className="max-w-5xl mx-auto px-4 py-16 text-center text-[#78716c]">Loading...</div>;
  if (!book) return <div className="max-w-5xl mx-auto px-4 py-16 text-center text-[#78716c]">Book not found</div>;

  const handlePurchase = (type) => {
    if (!reader) { toast.error('Please sign in to purchase books'); setSection('auth'); return; }
    purchaseMutation.mutate({ access_type: type });
  };

  const handleRead = () => {
    if (!reader) { toast.error('Please sign in to read'); setSection('auth'); return; }
    setSection(`read:${bookId}`);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="book-detail">
      <button onClick={() => setSection('store')} className="text-sm text-[var(--ms-primary,#7c5e3a)] flex items-center gap-1 mb-6 hover:underline">
        <ChevronLeft className="h-4 w-4" /> Back to Store
      </button>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Cover */}
        <div className="md:col-span-1">
          <div className="aspect-[3/4] bg-gradient-to-br from-[var(--ms-primary,#7c5e3a)] to-[var(--ms-dark,#0c0a09)] rounded-xl flex items-center justify-center overflow-hidden">
            {book.cover_image_url ? (
              <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-cover" />
            ) : (
              <div className="text-center p-6">
                <BookOpen className="h-16 w-16 text-[var(--ms-accent,#c9a227)]/60 mx-auto mb-3" />
                <p className="text-[var(--ms-accent,#c9a227)]/80 text-sm font-medium">{book.title}</p>
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="md:col-span-2 space-y-5">
          <div>
            <Badge className="bg-[var(--ms-accent,#c9a227)]/10 text-[var(--ms-primary,#7c5e3a)] mb-2">{book.category}</Badge>
            <h1 className="text-2xl font-bold text-[var(--ms-dark,#0c0a09)]">{book.title}</h1>
            <p className="text-[#78716c] mt-1">by {book.author}</p>
          </div>

          <div className="flex flex-wrap gap-4 text-sm text-[#78716c]">
            <span>{book.total_pages} pages</span>
            <span>{book.language}</span>
            {book.publisher && <span>{book.publisher}</span>}
            {book.publish_year > 0 && <span>{book.publish_year}</span>}
          </div>

          <p className="text-sm text-[#5C4A3A] leading-relaxed">{book.description}</p>

          {book.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {book.tags.map(t => <Badge key={t} variant="outline" className="text-[10px] border-[var(--ms-border-light,#e7e5e4)] text-[#78716c]">{t}</Badge>)}
            </div>
          )}

          {/* Purchase Actions */}
          <div className="bg-[#fafaf9] rounded-xl p-5 border border-[var(--ms-border-light,#e7e5e4)] space-y-3">
            {book.is_free ? (
              <div>
                <p className="text-green-700 font-bold text-lg">Free</p>
                <Button className="mt-2 bg-green-700 hover:bg-green-800 text-white" onClick={() => handlePurchase('purchase')} disabled={purchaseMutation.isPending} data-testid="get-free-btn">
                  {purchaseMutation.isPending ? 'Processing...' : 'Get Free Access'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-[var(--ms-primary,#7c5e3a)] font-bold text-xl">${book.price}</p>
                    <p className="text-xs text-[#78716c]">Purchase — Own forever</p>
                  </div>
                  {book.rental_price > 0 && (
                    <div className="border-l border-[var(--ms-border-light,#e7e5e4)] pl-4">
                      <p className="text-[var(--ms-primary,#7c5e3a)] font-bold text-lg">${book.rental_price}</p>
                      <p className="text-xs text-[#78716c]">Rent — {book.rental_days} days</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button className="bg-[var(--ms-primary,#7c5e3a)] hover:bg-[var(--ms-primary-hover,#5c4628)] text-white" onClick={() => handlePurchase('purchase')} disabled={purchaseMutation.isPending} data-testid="purchase-btn">
                    <ShoppingCart className="h-4 w-4 mr-1" /> {purchaseMutation.isPending ? 'Processing...' : 'Purchase'}
                  </Button>
                  {book.rental_price > 0 && (
                    <Button variant="outline" className="border-[var(--ms-primary,#7c5e3a)] text-[var(--ms-primary,#7c5e3a)]" onClick={() => handlePurchase('rental')} disabled={purchaseMutation.isPending} data-testid="rent-btn">
                      <Clock className="h-4 w-4 mr-1" /> Rent
                    </Button>
                  )}
                </div>
              </div>
            )}
            {reader && (
              <Button variant="outline" className="w-full mt-2" onClick={handleRead} data-testid="read-btn">
                <Eye className="h-4 w-4 mr-1" /> Read Sample / Full Book
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== READER (DRM Enhanced) ====================
const ReaderSection = ({ bookId, setSection }) => {
  const [page, setPage] = useState(1);
  const [isBlurred, setIsBlurred] = useState(false);
  const { reader } = useReader();
  const { data: readData, isLoading } = useQuery({
    queryKey: ['read-book', bookId, page],
    queryFn: () => readerApi(`/book/${bookId}/read?page=${page}`),
  });

  const progressMutation = useMutation({
    mutationFn: (data) => readerApi(`/book/${bookId}/progress`, { method: 'PUT', body: JSON.stringify(data) }),
  });

  useEffect(() => {
    if (readData && page > 1) {
      progressMutation.mutate({ current_page: page, bookmarks: readData.bookmarks || [] });
    }
  }, [page]);

  useEffect(() => {
    if (readData?.saved_page && readData.saved_page > 1 && page === 1) {
      setPage(readData.saved_page);
    }
  }, [readData?.saved_page]);

  // === DRM: Anti-screenshot & content protection ===
  useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e) => { e.preventDefault(); return false; };
    // Disable print screen & common screenshot shortcuts
    const handleKeyDown = (e) => {
      if (e.key === 'PrintScreen' || (e.ctrlKey && e.key === 'p') || (e.metaKey && e.shiftKey && (e.key === '3' || e.key === '4' || e.key === '5'))) {
        e.preventDefault();
        setIsBlurred(true);
        setTimeout(() => setIsBlurred(false), 2000);
      }
      // Disable Ctrl+S, Ctrl+U
      if (e.ctrlKey && (e.key === 's' || e.key === 'u')) { e.preventDefault(); }
    };
    // Blur content on window blur (tab switch / screenshot tools)
    const handleBlur = () => setIsBlurred(true);
    const handleFocus = () => setIsBlurred(false);
    // DevTools detection
    const detectDevTools = () => {
      const threshold = 160;
      const w = window.outerWidth - window.innerWidth > threshold;
      const h = window.outerHeight - window.innerHeight > threshold;
      if (w || h) setIsBlurred(true);
      else if (document.hasFocus()) setIsBlurred(false);
    };
    const devToolsInterval = setInterval(detectDevTools, 1000);

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      clearInterval(devToolsInterval);
    };
  }, []);

  const totalPages = readData?.total_pages || 0;
  const hasAccess = readData?.has_access;
  const samplePages = readData?.sample_pages || 3;

  const toggleBookmark = () => {
    const bm = readData?.bookmarks || [];
    const newBm = bm.includes(page) ? bm.filter(b => b !== page) : [...bm, page];
    progressMutation.mutate({ current_page: page, bookmarks: newBm });
  };

  // Watermark text
  const watermarkText = reader ? `${reader.email} | ${new Date().toISOString().split('T')[0]}` : '';

  return (
    <div className="min-h-screen bg-[var(--ms-dark-3,#0a0a0a)] flex flex-col select-none" data-testid="reader-section"
      style={{ WebkitUserSelect: 'none', MozUserSelect: 'none', userSelect: 'none' }}>
      {/* DRM Blur Overlay */}
      {isBlurred && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" data-testid="drm-blur-overlay">
          <div className="text-center">
            <Shield className="h-16 w-16 text-[var(--ms-accent,#c9a227)] mx-auto mb-4" />
            <h2 className="text-white text-xl font-bold mb-2">Content Protected</h2>
            <p className="text-[var(--ms-text-light,#e7e5e4)] text-sm">This content is protected by DRM. Screenshots are not allowed.</p>
            <p className="text-[#78716c] text-xs mt-2">Click anywhere to continue reading</p>
          </div>
        </div>
      )}

      {/* Reader Header */}
      <div className="bg-[var(--ms-primary,#0c0a09)] border-b border-[var(--ms-border,#292524)] px-4 py-2 flex items-center justify-between">
        <button onClick={() => setSection(`book:${bookId}`)} className="text-[var(--ms-text-light,#e7e5e4)] flex items-center gap-1 text-sm hover:text-white">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <div className="text-center">
          <p className="text-white text-xs font-medium">{readData?.title}</p>
          <p className="text-[#78716c] text-[10px]">Page {page} of {totalPages}</p>
        </div>
        <div className="flex items-center gap-2">
          <Shield className="h-3.5 w-3.5 text-[var(--ms-accent,#c9a227)]" title="DRM Protected" />
          <button onClick={toggleBookmark} className={`${readData?.bookmarks?.includes(page) ? 'text-[var(--ms-accent,#c9a227)]' : 'text-[#78716c]'} hover:text-[var(--ms-accent,#c9a227)]`} data-testid="bookmark-btn">
            <Bookmark className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Page Content with DRM */}
      <div className="flex-1 flex items-center justify-center p-4 relative">
        {isLoading ? (
          <div className="text-[#78716c]">Loading page...</div>
        ) : !hasAccess && page > samplePages ? (
          <div className="text-center max-w-md">
            <Shield className="h-12 w-12 text-[var(--ms-accent,#c9a227)]/40 mx-auto mb-4" />
            <h2 className="text-white text-lg font-bold mb-2">Sample Preview Ended</h2>
            <p className="text-[var(--ms-text-light,#e7e5e4)] text-sm mb-4">You've reached the end of the free sample. Purchase or rent to continue reading.</p>
            <Button className="bg-[var(--ms-accent,#c9a227)] text-[var(--ms-dark,#0c0a09)]" onClick={() => setSection(`book:${bookId}`)}>
              <ShoppingCart className="h-4 w-4 mr-1" /> Get Full Access
            </Button>
          </div>
        ) : (
          <div className="relative">
            {readData?.page_image_url ? (
              <img src={readData.page_image_url.startsWith('/api/') ? `${API_URL}${readData.page_image_url}?token=${localStorage.getItem('manjoro_reader_token') || ''}` : readData.page_image_url} alt={`Page ${page}`}
                className="max-h-[70vh] rounded shadow-lg pointer-events-none"
                style={{ userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false}
                onDragStart={e => e.preventDefault()} />
            ) : (
              <div className="bg-[#fafaf9] rounded-xl p-8 sm:p-12 max-w-2xl w-full min-h-[400px] shadow-xl flex items-center justify-center">
                <div className="text-center">
                  <BookOpen className="h-10 w-10 text-[var(--ms-border-light,#e7e5e4)] mx-auto mb-3" />
                  <p className="text-[#78716c] text-sm">Page {page}</p>
                  <p className="text-[#C4B49A] text-xs mt-1">
                    {hasAccess ? 'Full content available — page images not yet uploaded' : `Sample page ${page} of ${samplePages}`}
                  </p>
                </div>
              </div>
            )}
            {/* Watermark Overlay */}
            {watermarkText && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.04]" data-testid="watermark-overlay">
                <div className="w-full h-full flex flex-wrap items-center justify-center gap-16" style={{ transform: 'rotate(-30deg)', transformOrigin: 'center' }}>
                  {Array.from({ length: 12 }).map((_, i) => (
                    <span key={i} className="text-white text-xs whitespace-nowrap font-mono">{watermarkText}</span>
                  ))}
                </div>
              </div>
            )}
            {/* Invisible overlay to prevent drag/select */}
            <div className="absolute inset-0" style={{ background: 'transparent' }} />
          </div>
        )}
      </div>

      {/* Page Controls */}
      <div className="bg-[var(--ms-primary,#0c0a09)] border-t border-[var(--ms-border,#292524)] px-4 py-3 flex items-center justify-between">
        <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
          className="text-[var(--ms-text-light,#e7e5e4)] hover:text-white disabled:opacity-30" data-testid="prev-page-btn">
          <ChevronLeft className="h-4 w-4 mr-1" /> Previous
        </Button>
        <div className="flex items-center gap-2">
          <input type="number" min={1} max={totalPages || 999} value={page} onChange={e => setPage(Math.max(1, parseInt(e.target.value) || 1))}
            className="w-12 text-center text-sm bg-[var(--ms-dark-2,#1c1917)] border border-[var(--ms-border,#292524)] text-white rounded py-1" data-testid="page-input" />
          <span className="text-[#78716c] text-xs">/ {totalPages}</span>
        </div>
        <Button variant="ghost" size="sm" disabled={page >= totalPages && totalPages > 0} onClick={() => setPage(p => p + 1)}
          className="text-[var(--ms-text-light,#e7e5e4)] hover:text-white disabled:opacity-30" data-testid="next-page-btn">
          Next <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Progress Bar */}
      {totalPages > 0 && (
        <div className="h-1 bg-[var(--ms-dark-2,#1c1917)]">
          <div className="h-full bg-[var(--ms-accent,#c9a227)] transition-all" style={{ width: `${(page / totalPages) * 100}%` }} />
        </div>
      )}

      {/* DRM Notice */}
      <div className="bg-[var(--ms-primary,#0c0a09)] border-t border-[var(--ms-border,#292524)] px-4 py-1 text-center">
        <p className="text-[10px] text-[#5C4A3A] flex items-center justify-center gap-1">
          <Shield className="h-2.5 w-2.5" /> DRM protected. Content watermarked for account: {reader?.email}
        </p>
      </div>
    </div>
  );
};

// ==================== MY LIBRARY ====================
const LibrarySection = ({ setSection }) => {
  const { data: library = [], isLoading } = useQuery({ queryKey: ['reader-library'], queryFn: () => readerApi('/library') });

  if (isLoading) return <div className="max-w-5xl mx-auto px-4 py-16 text-center text-[#78716c]">Loading library...</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6" data-testid="library-section">
      <div>
        <h1 className="text-2xl font-bold text-[var(--ms-dark,#0c0a09)]">My Library</h1>
        <p className="text-sm text-[#78716c]">Books you own or rent</p>
      </div>
      {library.length === 0 ? (
        <div className="text-center py-16">
          <BookMarked className="h-12 w-12 text-[var(--ms-border-light,#e7e5e4)] mx-auto mb-3" />
          <p className="text-[#78716c]">Your library is empty</p>
          <Button className="mt-4 bg-[var(--ms-primary,#7c5e3a)] text-white" onClick={() => setSection('store')}>Browse Books</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {library.map(book => (
            <div key={book.id} className="bg-white rounded-xl border border-[var(--ms-border-light,#e7e5e4)] overflow-hidden" data-testid={`library-book-${book.id}`}>
              <div className="h-36 bg-gradient-to-br from-[var(--ms-primary,#7c5e3a)] to-[var(--ms-dark,#0c0a09)] flex items-center justify-center">
                {book.cover_image_url ? (
                  <img src={book.cover_image_url} alt={book.title} className="w-full h-full object-cover" />
                ) : (
                  <BookOpen className="h-10 w-10 text-[var(--ms-accent,#c9a227)]/60" />
                )}
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-sm text-[var(--ms-dark,#0c0a09)]">{book.title}</h3>
                <p className="text-xs text-[#78716c]">{book.author}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="text-[10px] capitalize">{book.access_type}</Badge>
                  {book.expires_at && <span className="text-[10px] text-[#78716c]">Expires: {new Date(book.expires_at).toLocaleDateString()}</span>}
                </div>
                {book.current_page > 1 && (
                  <div className="mt-2">
                    <div className="w-full bg-[var(--ms-border-light,#e7e5e4)] rounded-full h-1.5">
                      <div className="bg-[var(--ms-accent,#c9a227)] h-full rounded-full" style={{ width: `${(book.current_page / (book.total_pages || 1)) * 100}%` }} />
                    </div>
                    <p className="text-[10px] text-[#78716c] mt-0.5">Page {book.current_page} of {book.total_pages}</p>
                  </div>
                )}
                <Button size="sm" className="w-full mt-3 bg-[var(--ms-primary,#7c5e3a)] text-white hover:bg-[var(--ms-primary-hover,#5c4628)]" onClick={() => setSection(`read:${book.id}`)}>
                  <Eye className="h-3.5 w-3.5 mr-1" /> {book.current_page > 1 ? 'Continue Reading' : 'Start Reading'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== AUTH ====================
const AuthSection = ({ onLogin, setSection }) => {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const loginMutation = useMutation({
    mutationFn: (data) => fetch(`${API_URL}/api/books/public/${activeTenantId}/auth/login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    }).then(r => { if (!r.ok) throw r; return r.json(); }),
    onSuccess: (d) => { onLogin(d); toast.success('Welcome back!'); setSection('library'); },
    onError: () => toast.error('Invalid credentials'),
  });

  const registerMutation = useMutation({
    mutationFn: (data) => fetch(`${API_URL}/api/books/public/${activeTenantId}/auth/register`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data),
    }).then(r => { if (!r.ok) throw r; return r.json(); }),
    onSuccess: (d) => { onLogin(d); toast.success('Account created!'); setSection('store'); },
    onError: () => toast.error('Registration failed — email may already be registered'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === 'login') loginMutation.mutate({ email, password });
    else registerMutation.mutate({ email, password, name });
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="auth-section">
      <div className="bg-white rounded-xl border border-[var(--ms-border-light,#e7e5e4)] p-6 shadow-sm">
        <div className="text-center mb-6">
          <BookOpen className="h-8 w-8 text-[var(--ms-accent,#c9a227)] mx-auto mb-2" />
          <h2 className="text-lg font-bold text-[var(--ms-dark,#0c0a09)]">{mode === 'login' ? 'Sign In' : 'Create Account'}</h2>
          <p className="text-xs text-[#78716c]">{mode === 'login' ? 'Access your library and continue reading' : 'Join to purchase and read books'}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          {mode === 'register' && (
            <div><Label className="text-xs">Name</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" data-testid="auth-name" /></div>
          )}
          <div><Label className="text-xs">Email</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="auth-email" /></div>
          <div><Label className="text-xs">Password</Label><Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Your password" required data-testid="auth-password" /></div>
          <Button type="submit" className="w-full bg-[var(--ms-primary,#7c5e3a)] text-white hover:bg-[var(--ms-primary-hover,#5c4628)]" disabled={loginMutation.isPending || registerMutation.isPending} data-testid="auth-submit">
            {(loginMutation.isPending || registerMutation.isPending) ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </Button>
        </form>
        <p className="text-center text-xs text-[#78716c] mt-4">
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-[var(--ms-primary,#7c5e3a)] font-medium hover:underline" data-testid="toggle-auth-mode">
            {mode === 'login' ? 'Register' : 'Sign In'}
          </button>
        </p>
      </div>
    </div>
  );
};

// ==================== FOOTER ====================
const StoreFooter = ({ setSection }) => {
  const { data: settings } = useQuery({ queryKey: ['book-settings'], queryFn: () => pubApi('/settings') });
  const storeName = settings?.store_name || 'ABMB Books';
  const tagline = settings?.store_tagline || 'Transformative Christian Literature';
  return (
    <footer className="bg-[var(--ms-primary,#0c0a09)] text-[var(--ms-text-light,#e7e5e4)] py-10 border-t border-[var(--ms-border,#292524)]">
      <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="h-5 w-5 text-[var(--ms-accent,#c9a227)]" />
            <span className="font-bold text-[var(--ms-accent,#c9a227)]" data-testid="footer-store-name">{storeName}</span>
          </div>
          <p className="text-xs text-[#78716c]">{tagline}</p>
        </div>
        <div>
          <h4 className="font-semibold text-white text-sm mb-2">Quick Links</h4>
          <div className="space-y-1 text-sm">
            {['home', 'store'].map(s => (
              <button key={s} onClick={() => setSection(s)} className="block text-[#78716c] hover:text-white capitalize">{s}</button>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-semibold text-white text-sm mb-2">Support</h4>
          {settings?.contact_email ? (
            <a href={`mailto:${settings.contact_email}`} className="text-xs text-[#78716c] hover:text-white" data-testid="footer-contact-email">{settings.contact_email}</a>
          ) : (
            <p className="text-xs text-[#78716c]">Contact us for any inquiries about books or your account.</p>
          )}
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-4 mt-6 pt-4 border-t border-[var(--ms-border,#292524)] text-center text-[10px] text-[#5C4A3A]" data-testid="footer-bottom-text">
        {settings?.footer_text || `${storeName}. All rights reserved.`}
      </div>
    </footer>
  );
};

// ==================== MAIN ====================
// Resolves the store settings once and injects them as CSS custom properties
// onto the root container so every nested element can read the configured
// brand colour via Tailwind arbitrary values like text-[var(--ms-accent)].
const useStoreSettings = () => {
  const { data } = useQuery({
    queryKey: ['ms-store-settings'],
    queryFn: () => pubApi('/settings'),
    staleTime: 5 * 60 * 1000,
  });
  return data || {};
};

const ManjaroBooksPage = () => {
  const params = useParams();
  const slug = params.slug || '';
  const directTenantId = params.tenantId || '';
  const qc = useQueryClient();
  const [section, setSection] = useState('home');
  const [resolving, setResolving] = useState(!!slug);
  const [tenantReady, setTenantReady] = useState(!slug);  // legacy alias is ready immediately
  const { reader, login, logout, isLoggedIn } = useReader();
  const settings = useStoreSettings();
  const lastTenantRef = useRef(null);

  // Resolve slug → tenant_id on mount (or whenever slug changes).
  useEffect(() => {
    const applyTenant = (id) => {
      if (lastTenantRef.current !== id) {
        // Wipe cached public/reader data so freshly-mounted queries refetch
        // with the new tenant's data instead of returning stale results from
        // the previous (or default) tenant.
        qc.removeQueries({ predicate: q => {
          const k = q.queryKey?.[0];
          return typeof k === 'string' && (k.startsWith('book-') || k === 'ms-store-settings' || k === 'reader-library');
        }});
        setActiveTenantId(id);
        lastTenantRef.current = id;
      }
      setTenantReady(true);
    };
    if (directTenantId) {
      // Direct tenant URL: /manjoro-books/:tenantId — used by school admins to preview their store
      applyTenant(directTenantId);
      setResolving(false);
      return;
    }
    if (!slug) {
      // Legacy /manjoro-books alias — use the default hard-coded tenant.
      applyTenant(DEFAULT_TENANT_ID);
      return;
    }
    setResolving(true);
    setTenantReady(false);
    fetch(`${API_URL}/api/books/public/by-slug/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Store not found')))
      .then(d => {
        applyTenant(d.tenant_id);
        setResolving(false);
      })
      .catch(() => {
        setResolving(false);
        setTenantReady(false);
      });
  }, [slug, directTenantId, qc]);

  useEffect(() => { window.scrollTo(0, 0); }, [section]);

  // Parse composite sections like "book:id" and "read:id"
  const parsedSection = section.startsWith('book:') ? 'book-detail' :
                         section.startsWith('read:') ? 'reader' : section;
  const entityId = section.includes(':') ? section.split(':')[1] : null;

  const renderSection = () => {
    switch (parsedSection) {
      case 'store': return <StoreSection setSection={setSection} />;
      case 'book-detail': return <BookDetailSection bookId={entityId} setSection={setSection} reader={reader} />;
      case 'reader': return <ReaderSection bookId={entityId} setSection={setSection} />;
      case 'library': return isLoggedIn ? <LibrarySection setSection={setSection} /> : <AuthSection onLogin={login} setSection={setSection} />;
      case 'auth': return <AuthSection onLogin={login} setSection={setSection} />;
      default: return <HomeSection setSection={setSection} />;
    }
  };

  // Theme CSS variables driven entirely by the admin's book_settings doc.
  // Iter 316 — full palette wired (dark/surface/text shades) so editing colors
  // in /admin actually rebrands the page instead of only flipping accents.
  const themeStyle = {
    '--ms-primary':       settings?.primary_color    || '#7c5e3a',
    '--ms-accent':        settings?.accent_color     || '#c9a227',
    '--ms-dark':          settings?.dark_color       || '#0c0a09',
    '--ms-surface':       settings?.surface_color    || '#fafaf9',
    '--ms-text-light':    settings?.text_light_color || '#e7e5e4',
    '--ms-text-muted':    settings?.text_muted_color || '#78716c',
    // Derived/secondary shades — fall back to the legacy palette if not
    // overridden. Admins can configure these separately in a future iter.
    '--ms-dark-2':        settings?.dark_2_color       || '#1c1917',
    '--ms-dark-3':        settings?.dark_3_color       || '#0a0a0a',
    '--ms-border':        settings?.border_color       || '#292524',
    '--ms-border-light':  settings?.border_light_color || '#e7e5e4',
    '--ms-primary-hover': settings?.primary_hover_color|| '#5c4628',
    '--ms-accent-hover':  settings?.accent_hover_color || '#a98317',
  };
  if (typeof document !== 'undefined') {
    document.title = settings?.store_name ? `${settings.store_name} · Books` : 'ABMB Books';
  }

  // Reader view is fullscreen without nav/footer
  if (parsedSection === 'reader') return renderSection();

  // Loading + not-found states for slug routes
  if (resolving) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center" data-testid="store-resolving">
        <p className="text-sm text-[#78716c]">Loading store…</p>
      </div>
    );
  }
  if (slug && !tenantReady) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center" data-testid="store-not-found">
        <div className="text-center">
          <BookOpen className="h-10 w-10 text-[var(--ms-primary,#7c5e3a)] mx-auto mb-3" />
          <h1 className="text-xl font-bold text-[var(--ms-dark,#0c0a09)]">Store not found</h1>
          <p className="text-sm text-[#78716c] mt-2">No store is configured at <span className="font-mono">/store/{slug}</span>.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafaf9]" data-testid="manjoro-books" data-store-theme style={themeStyle}>
      <StoreNav section={parsedSection} setSection={setSection} reader={reader} logout={logout} />
      {renderSection()}
      <StoreFooter setSection={setSection} />
    </div>
  );
};

export default ManjaroBooksPage;
